import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { addHistory } from './preferences';
import { usePwa } from './usePwa';
import { useTheme } from './useTheme';
import { AppShell, type AppRoute } from '../components/AppShell';
import { Button } from '../design-system/Button';
import { createProcessingLimits, inferProcessingProfile } from '../engine/core/limits';
import type { AnalysisReport, ExtractionResult, ProgressUpdate, RepairResult, StructuredError } from '../engine/core/types';
import { AnalysisWorkspace } from '../features/analysis/AnalysisWorkspace';
import { FileDetails } from '../features/analysis/FileDetails';
import { CapabilityMatrix } from '../features/capabilities/CapabilityMatrix';
import { ExtractWorkspace } from '../features/extract/ExtractWorkspace';
import { ReportsPage } from '../features/reports/ReportsPage';
import { ErrorPanel } from '../features/rescue/ErrorPanel';
import { FileDropzone } from '../features/rescue/FileDropzone';
import { ProcessingPanel } from '../features/rescue/ProcessingPanel';
import { ExtractionResultView } from '../features/results/ExtractionResultView';
import { RepairResultView } from '../features/results/RepairResultView';
import { HelpPage } from '../features/static/HelpPage';
import { PrivacyPage } from '../features/static/PrivacyPage';
import { SettingsPage } from '../features/static/SettingsPage';
import { UnavailablePage } from '../features/static/UnavailablePage';
import { WelcomePage } from '../features/welcome/WelcomePage';
import { WorkerManager, WorkerOperationError } from '../workers/WorkerManager';
import { formatBytes } from '../utilities/format';

type SpecialView = 'none' | 'processing' | 'error' | 'repair-result' | 'extraction-result';

const pathToRoute: Record<string, AppRoute> = {
  '/': 'home',
  '/rescue': 'rescue',
  '/inspect': 'inspect',
  '/extract': 'extract',
  '/clean': 'clean',
  '/compare': 'compare',
  '/batch': 'batch',
  '/reports': 'reports',
  '/help': 'help',
  '/privacy': 'privacy',
  '/settings': 'settings',
  '/excel-repair': 'rescue',
  '/excel-unreadable-content': 'rescue',
  '/recover-corrupt-xlsx': 'rescue',
  '/word-repair': 'rescue',
  '/powerpoint-repair': 'rescue',
  '/zip-recovery': 'rescue',
  '/pdf-repair': 'rescue',
  '/odt-recovery': 'rescue',
};

const routeToPath: Record<AppRoute, string> = {
  home: '/', rescue: '/rescue', inspect: '/inspect', extract: '/extract', clean: '/clean', compare: '/compare', batch: '/batch', reports: '/reports', help: '/help', privacy: '/privacy', settings: '/settings',
};

export function App(): React.JSX.Element {
  const [route, setRoute] = useState<AppRoute>(() => pathToRoute[window.location.pathname] ?? 'home');
  const [theme, setTheme] = useTheme();
  const pwa = usePwa();
  const limits = useState(() => createProcessingLimits(inferProcessingProfile()))[0];
  const manager = useRef(new WorkerManager());
  const operationToken = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<AnalysisReport | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [specialView, setSpecialView] = useState<SpecialView>('none');
  const [error, setError] = useState<StructuredError | null>(null);
  const [repairResult, setRepairResult] = useState<RepairResult | null>(null);
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);

  useEffect(() => {
    const pop = (): void => {
      setRoute(pathToRoute[window.location.pathname] ?? 'home');
      setSpecialView('none');
    };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);

  useEffect(() => () => manager.current.cancel(), []);

  const navigate = useCallback((target: AppRoute): void => {
    setRoute(target);
    setSpecialView('none');
    setError(null);
    window.history.pushState({}, '', routeToPath[target]);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const reset = useCallback((): void => {
    operationToken.current += 1;
    manager.current.cancel();
    setFile(null);
    setReport(null);
    setProgress(null);
    setError(null);
    setRepairResult(null);
    setExtractionResult(null);
    setSpecialView('none');
    setRoute('rescue');
    window.history.pushState({}, '', '/rescue');
  }, []);

  const analyze = useCallback(async (selected: File): Promise<void> => {
    if (selected.size > limits.maxFileBytes) {
      setError({
        code: 'FILE_SIZE_LIMIT',
        title: 'This file exceeds the active device limit',
        message: `${selected.name} is ${formatBytes(selected.size)}. The ${limits.profile} profile allows ${formatBytes(limits.maxFileBytes)}.`,
        technical: `File bytes ${selected.size}; configured limit ${limits.maxFileBytes}.`,
        recommendation: 'Use a desktop device with more available memory, and close memory-intensive applications.',
        recoverable: false,
      });
      setFile(selected);
      setSpecialView('error');
      return;
    }
    const token = ++operationToken.current;
    setFile(selected);
    setProgress(null);
    setError(null);
    setSpecialView('processing');
    try {
      const nextReport = await manager.current.analyze(selected, limits, setProgress);
      if (token !== operationToken.current) return;
      setReport(nextReport);
      setRoute('inspect');
      window.history.pushState({}, '', '/inspect');
      setSpecialView('none');
      addHistory({ timestamp: nextReport.completedAt, format: nextReport.detected.format, operation: 'inspect', result: 'analyzed', durationMs: nextReport.durationMs, issueCount: nextReport.issues.length });
    } catch (caught) {
      if (token !== operationToken.current) return;
      setError(workerError(caught));
      setSpecialView('error');
    }
  }, [limits]);

  const runRepair = useCallback(async (): Promise<void> => {
    if (!file) return;
    const token = ++operationToken.current;
    setProgress(null);
    setSpecialView('processing');
    try {
      const result = await manager.current.repair(file, limits, setProgress);
      if (token !== operationToken.current) return;
      setRepairResult(result);
      setReport(result.validated);
      setFile(new File([result.output], result.outputName, { type: result.outputMime, lastModified: Date.now() }));
      setSpecialView('repair-result');
      addHistory({ timestamp: result.validated.completedAt, format: result.validated.detected.format, operation: 'safe-repair', result: result.outcome, durationMs: result.validated.durationMs, issueCount: result.validated.issues.length });
    } catch (caught) {
      if (token !== operationToken.current) return;
      setError(workerError(caught));
      setSpecialView('error');
    }
  }, [file, limits]);

  const runExtraction = useCallback(async (): Promise<void> => {
    if (!file) return;
    const token = ++operationToken.current;
    setProgress(null);
    setSpecialView('processing');
    try {
      const result = await manager.current.extract(file, limits, setProgress);
      if (token !== operationToken.current) return;
      setExtractionResult(result);
      setSpecialView('extraction-result');
      addHistory({ timestamp: result.report.completedAt, format: result.report.detected.format, operation: 'extract-only', result: result.outcome, durationMs: result.report.durationMs, issueCount: result.report.issues.length });
    } catch (caught) {
      if (token !== operationToken.current) return;
      setError(workerError(caught));
      setSpecialView('error');
    }
  }, [file, limits]);

  const cancel = (): void => {
    operationToken.current += 1;
    manager.current.cancel();
    setProgress(null);
    setSpecialView('none');
    setRoute(report ? 'inspect' : 'rescue');
  };

  const content = (() => {
    if (specialView === 'processing' && file) return <ProcessingPanel file={file} progress={progress} onCancel={cancel} />;
    if (specialView === 'error' && error) return <ErrorPanel error={error} onReset={reset} />;
    if (specialView === 'repair-result' && repairResult) return <RepairResultView result={repairResult} onInspect={() => setSpecialView('none')} onReset={reset} />;
    if (specialView === 'extraction-result' && extractionResult) return <ExtractionResultView result={extractionResult} onInspect={() => { setRoute('inspect'); setSpecialView('none'); }} onReset={reset} />;
    if (route === 'home') return <WelcomePage onFile={(selected) => { void analyze(selected); }} onOpenWorkspace={() => navigate('rescue')} />;
    if (route === 'rescue') return <RescueHome limits={limits} onFile={(selected) => { void analyze(selected); }} />;
    if (route === 'inspect' && report) return <AnalysisWorkspace report={report} onRepair={() => { void runRepair(); }} onExtract={() => { void runExtraction(); }} onReset={reset} />;
    if (route === 'extract' && report) return <ExtractWorkspace report={report} onExtract={() => { void runExtraction(); }} />;
    if (route === 'reports' && report) return <ReportsPage report={report} />;
    if (route === 'help') return <HelpPage />;
    if (route === 'privacy') return <PrivacyPage />;
    if (route === 'settings') return <SettingsPage theme={theme} setTheme={setTheme} limits={limits} />;
    if (route === 'clean' || route === 'compare' || route === 'batch') return <UnavailablePage feature={route} onRescue={() => navigate('rescue')} />;
    return <RescueHome limits={limits} onFile={(selected) => { void analyze(selected); }} />;
  })();

  const showContext = report && route === 'inspect' && specialView !== 'processing' && specialView !== 'error';
  return (
    <AppShell route={route} navigate={navigate} hasReport={report !== null} theme={theme} setTheme={setTheme} online={pwa.online} context={showContext ? <FileDetails report={report} /> : undefined}>
      {pwa.updateReady ? <div className="update-banner"><span>A verified MendByte update is ready.</span><Button variant="quiet" onClick={pwa.applyUpdate}><RefreshCw />Reload safely</Button></div> : null}
      {content}
    </AppShell>
  );
}

function RescueHome({ limits, onFile }: { limits: ReturnType<typeof createProcessingLimits>; onFile: (file: File) => void }): React.JSX.Element {
  return (
    <div className="rescue-home">
      <section className="rescue-hero">
        <div>
          <h1>Your file may not be lost yet.</h1>
          <p>Diagnose, repair, and recover damaged files directly on your device.</p>
        </div>
        <p className="rescue-hero__note"><strong>Private File Repair & Recovery</strong><span>ODT, PDF, ZIP, PowerPoint, Word, and Excel recovery runs locally on this device.</span></p>
      </section>
      <FileDropzone onFile={onFile} limits={limits} />
      <section className="how-it-works">
        <h2>Transparent from diagnosis to download</h2>
        <ol>
          <li><span>01</span><strong>Local safety preflight</strong><p>Hostile archive limits are checked before decompression.</p></li>
          <li><span>02</span><strong>Evidence-based analysis</strong><p>Every health deduction maps to a concrete detected condition.</p></li>
          <li><span>03</span><strong>Preserve-first recovery</strong><p>Outputs are offered only after re-opening and validation.</p></li>
        </ol>
      </section>
      <CapabilityMatrix />
    </div>
  );
}

function workerError(caught: unknown): StructuredError {
  if (caught instanceof WorkerOperationError) return caught.details;
  return {
    code: 'UI_OPERATION_ERROR',
    title: 'MendByte could not complete this operation',
    message: caught instanceof Error ? caught.message : 'An unexpected local error occurred.',
    technical: String(caught),
    recommendation: 'Keep the original file and try again.',
    recoverable: true,
  };
}
